import { Component, computed, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, startWith } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { WalletService } from '../../../core/services/wallet.service';
import { AuthService } from '../../../core/auth/auth.service';
import { MerchantContextService } from '../../../core/services/merchant-context.service';

interface NavItem {
  label: string;
  icon: string;
  route?: string;
  children?: { label: string; route: string }[];
  expanded?: boolean;
  isLogout?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, MatIconModule, MatSlideToggleModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent {
  wallet = inject(WalletService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private context = inject(MerchantContextService);

  // Six sections. Everything the app can do hangs off one of these; anything not listed
  // here (bank accounts, cards, add-shop, …) is reachable from inside its section's page,
  // not from the sidebar — that's what keeps this list short. Application (the BizCombo client
  // application) sits beside the six.
  navItems: NavItem[] = [
    { label: 'Dashboard', icon: 'dashboard', route: '/dashboard' },
    {
      // BC_Onboarding_UI's client application (features/registration). The introducer and
      // migration pages are reached from inside it, as they are there.
      label: 'Application', icon: 'assignment', expanded: false, children: [
        { label: 'Application form', route: '/application/form' },
        { label: 'Status',           route: '/application/individual-dashboard' },
        { label: 'Account',          route: '/application/account' },
      ]
    },
    {
      label: 'Treasury', icon: 'account_balance_wallet', expanded: false, children: [
        { label: 'Holdings', route: '/treasury/holdings' },
        { label: 'Activity', route: '/treasury/activity' },
      ]
    },
    {
      label: 'Business', icon: 'corporate_fare', expanded: false, children: [
        { label: 'Overview', route: '/business/overview' },
        { label: 'KlidiPay', route: '/business/klidipay' },
      ]
    },
    {
      label: 'Reports', icon: 'assessment', expanded: false, children: [
        { label: 'Intelligence', route: '/reports/intelligence' },
        { label: 'eStatements',  route: '/reports/estatements' },
        { label: 'Performance',  route: '/reports/performance' },
        { label: 'Fees',         route: '/reports/fees' },
        { label: 'Affiliate',    route: '/reports/affiliate' },
      ]
    },
    {
      label: 'Compliance', icon: 'verified_user', expanded: false, children: [
        { label: 'Verification', route: '/compliance/verification' },
        { label: 'Security',     route: '/compliance/security' },
        { label: 'Documents',    route: '/compliance/documents' },
        { label: 'Audit log',    route: '/compliance/audit-log' },
      ]
    },
    {
      label: 'Settings', icon: 'settings', expanded: false, children: [
        { label: 'Profile',       route: '/settings/profile' },
        { label: 'API keys',      route: '/settings/api-keys' },
        { label: 'Notifications', route: '/settings/notifications' },
        { label: 'Two-factor',    route: '/settings/two-factor' },
        { label: 'API docs',      route: '/settings/api-docs' },
      ]
    },
    {
      // Kept out of the six product sections so it doesn't clutter them. BizCombo's own
      // tooling (KYC review, Scrypt, crypto gates) is in the CMS, not here.
      label: 'Platform', icon: 'tune', expanded: false, children: [
        { label: 'Administration',  route: '/administration' },
      ]
    },
    { label: 'Help & support', icon: 'help_outline', route: '/help-support' },
  ];

  /** The sections the active account has: Individual drops Business and Compliance. */
  visibleNavItems = computed(() => this.navItems.filter(item => this.context.showsSection(sectionOf(item))));

  // Logout is in the header user menu; nothing else lives at the bottom for now.
  bottomItems: NavItem[] = [];

  constructor() {
    // Every page now lives inside a collapsible group, so a group that stays shut on
    // arrival would hide the page you're actually on. Open the owning group on each
    // navigation; leave the others exactly as the user left them.
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(null)
    ).subscribe(() => {
      this.expandActiveGroup();
      // On mobile the sidebar is an overlay drawer. Leaving it open on top of the page you
      // just navigated to makes the app feel stuck — every tap would need a second one to
      // dismiss it. Harmless on desktop, where the drawer state is unused.
      this.wallet.closeMobileSidebar();
    });
  }

  /**
   * Opens the group owning the current page and closes the rest.
   *
   * Matches on the section segment (/treasury, /business, …) rather than the exact child route,
   * so detail pages that aren't in the sidebar — /treasury/deposit, /compliance/documents —
   * still open the group they belong to.
   */
  private expandActiveGroup(): void {
    const section = this.router.url.split('?')[0].split('/')[1];
    if (!section) return;

    const owner = this.navItems.find(
      item => item.children?.some(c => c.route.split('/')[1] === section));
    if (owner) this.openOnly(owner);
  }

  /**
   * Accordion, not independent toggles: with eight groups, leaving them all open produced a nav
   * list far taller than the viewport, so reaching Settings meant scrolling past everything else.
   */
  toggleItem(item: NavItem) {
    if (!item.children) return;
    if (item.expanded) item.expanded = false;
    else this.openOnly(item);
  }

  private openOnly(target: NavItem): void {
    for (const item of this.navItems) {
      if (item.children) item.expanded = item === target;
    }
  }

  logout() { this.auth.logout(); }

  get collapsed() { return this.wallet.sidebarCollapsed(); }

  /** Pinned is simply "not collapsed" — one state, two controls, so they stay in step. */
  get pinned() { return !this.wallet.sidebarCollapsed(); }

  setPinned(pinned: boolean) { this.wallet.setSidebarCollapsed(!pinned); }

  toggleSidebar() { this.wallet.toggleSidebar(); }
}

/** Top-level URL segment a nav item lives under: its own route, or its children's. */
function sectionOf(item: NavItem): string {
  return (item.route ?? item.children?.[0]?.route ?? '').split('/')[1] ?? '';
}
